import { EventEmitter, Injectable } from '@angular/core';
import { ParamMap, Router, UrlTree } from "@angular/router";
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from "@angular/common/http";
import { Observable } from 'rxjs/Observable';
import { environment } from "../environments/environment";
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import { IApiResponse } from "../Interfaces/IApiResponse";
import { IProductCategory } from "../Interfaces/IProductCategory";
import { IFilterCategory } from "../Interfaces/IFilterCategory";
import { IFilterResult } from "../Interfaces/IFilterResult";
import { IProductListingPanel } from "../Interfaces/IProductListingPanel";
import { IFilterOptions } from "../Interfaces/IFilterOptions";
import { IFilterResultCategoryBrowsing } from "../Interfaces/IFilterResultCategoryBrowsing";
import { IFilterCategoryRequestValue, IFilteringRequest } from "../Interfaces/IFilteringRequest";
import { PropertiesService } from "./properties.service";
import { ICategoryBrowseFilterOptions } from "../Interfaces/ICategoryBrowseFilterOptions";
import { IVariantGroupingType } from "../Interfaces/IVariantGroupingType";
import { IStyle } from "../Interfaces/IStyle";


// API endpoint URLs
const BROWSE_PRODUCTS_API_URL = '/api/kws/category-browse';
const PRODUCTS_LISTING_API_URL = '/api/kws/product-listing';
const PRODUCT_LISTING_PANEL_API_URL = '/api/kws/product-listing-panels';
// Speculative

@Injectable()
export class ProductsService {

    // External properties
    /**
     * Whether or not the service should update the query string as filters and filterOptions change. This is desirable
     * for features like product filtering and product family where you want certain states to be deep-linkable, but doesn't
     * make sense for other areas like product query results, where the product display is only one area of the page
     * @type {boolean}
     */
    updateUrlParams: boolean = false;

    // Events
    //updated$: EventEmitter<IFilterResult> = new EventEmitter<IFilterResult>();
    paramUpdated$: EventEmitter<any> = new EventEmitter<any>();
    cleared$: EventEmitter<any> = new EventEmitter<any>();
    categoryChanged$: EventEmitter<any> = new EventEmitter<any>();
    pageChanged$: EventEmitter<any> = new EventEmitter<any>();

    // Internal properties
    private httpClient: HttpClient;
    private router: Router;
    private categoryBrowseFilterOptions: ICategoryBrowseFilterOptions = {
        sort: "",
        tab: "",
        finish: "",
        type: ""
    };
    private SUPPRESSED_PARAMS = [''];
    currentRoute: string;
    activeTab: number;

    /**
     * A list of the currently-applied query parameters
     */
    private queryParams: any = {};

    constructor(private route: ActivatedRoute, router: Router, httpClient: HttpClient, private propertiesService: PropertiesService) {
        this.httpClient = httpClient;
        this.router = router;
    }

    loadFilterResults(category?, secondaryCategory?, groupby?, type?, selectedFinishes?) {
        if (!type) { type = ""; }
        if (!secondaryCategory) { secondaryCategory = ""; }
        if (!selectedFinishes) { selectedFinishes = ""; }
        return this.httpClient.get(`${BROWSE_PRODUCTS_API_URL}?region=US&language=EN&category=${category}&secondaryCategory=${secondaryCategory}&groupBy=${groupby}&type=${type}&selectedFinishes=${selectedFinishes}`);
    }

    // On filtering pages we don't want the filterOptions for type/tab to be set globally by the service
    // because it breaks functionality for the tagging,
    resetBrowseFilterOptions() {
        delete this.categoryBrowseFilterOptions['type'];
        delete this.categoryBrowseFilterOptions['tab'];
    }

    // Product Filtering
    /**
     * Requests the list of products for a particular category and filter set, or loads additional results
     * (by setting the options.startIndex and options.resultsPerPage parameters)
     * @param {IProductCategory} productCategory
     * @param {IFilterCategory[]} filters: If null, all products in this category are returned
     * @param {IFilterOptions} options: Additional options to control what products are loaded. If null, default options are applied.
     * @param {IFilterCategory[]} filters
     * @returns {IFilterResult}
     */
    getFilterResultsByType(type: string, category: IProductCategory | string, func: string, useFilters: boolean, page: number): Observable<IApiResponse<IFilterResult>> {
        // We did a filter query so we'll just assume the categoryBrowseFilter for sorting is garbage
        this.resetBrowseFilterOptions();

        let query = PRODUCTS_LISTING_API_URL;
        let tempFilterOpts;
        let request: IFilteringRequest;

        tempFilterOpts = this.getDirtyOptions();

        // tempFilterOpts.resultsPerPage = 20;
        tempFilterOpts.pageIndex = page;

        if (type == 'category') {
            let productCategoryId;
            if (typeof (category) === 'string') {
                productCategoryId = category;
            } else {
                productCategoryId = category.stepId;
            }
            request = {
                category: productCategoryId,
                browseByType: "Category",
                filterOptions: tempFilterOpts,
                filters: (useFilters ? this.getFilterCategoryRequestValues() : [])
            }
        } else if (type == 'function') {
            request = {
                function: func,
                browseByType: "Function",
                filterOptions: tempFilterOpts,
                filters: (useFilters ? this.getFilterCategoryRequestValues() : [])
            }
        } else {
            request = {
                browseByType: "AllProducts",
                filterOptions: tempFilterOpts,
                filters: (useFilters ? this.getFilterCategoryRequestValues() : [])
            }
        }

        return this.httpClient.post<IApiResponse<IFilterResult>>(query, request);
    }

    // Product Content Panels For Filtering
    getProductListingPanels(value: IProductCategory | string, queryType: string): Observable<IApiResponse<IProductListingPanel>> {
        let query = PRODUCT_LISTING_PANEL_API_URL;

        query += '?region=US&language=EN&' + queryType + '=' + value + '&browseByType=' + ((queryType).substr(0, 1).toUpperCase() + queryType.substring(1));

        return this.httpClient.get<IApiResponse<IProductListingPanel>>(query);

    }


    /**
     * Sets global filter options (sort, finish, type, tab etc.)
     * @param name
     * @param value
     */
    setFilterOption(name, value): void {
        if (this.categoryBrowseFilterOptions.hasOwnProperty(name)) {
            this.categoryBrowseFilterOptions[name] = value;

            // Certain params are not for general use and we don't want to expose them
            if (!this.isSuppressedParam(name)) {

                this.updateParam(name, value, true);
            }
        }
    }

    /**
     * Converts the list of query params into an array of IFilterCategoryRequestValues needed by the filtering endpoint
     * @returns {IFilterCategoryRequestValue[]}
     */
    getFilterCategoryRequestValues(): IFilterCategoryRequestValue[] {
        let values = [];

        // This is needed because the query params include filterOptions as well as filters
        const disallowedParams = ['showDiscontinued', 'proGrade'];

        for (let param in this.queryParams) {

            if (disallowedParams.indexOf(param) >= 0) {
                continue;
            }

            values.push({
                name: param,
                values: this.queryParams[param].split(',')
            });
        }

        return values;
    }

    getFilterOption(name): any {
        if (this.categoryBrowseFilterOptions.hasOwnProperty(name)) {
            return this.categoryBrowseFilterOptions[name];
        }

        return null;
    }

    // Check to make sure the filter option actually has a value, because the server will error if we pass empty values
    // Strip out the empty values before sending the request
    getDirtyOptions() {
        let optionsObj = this.getFilterOptions({});
        // Get a non-reference copy of the filter options to strip out before the request
        let options = JSON.parse(JSON.stringify(optionsObj));

        Object.getOwnPropertyNames(options).forEach(key => {
            if (options[key] == "") {
                delete options[key]
            }
        });

        return options;
    }

    isSuppressedParam(name) {
        return this.SUPPRESSED_PARAMS.indexOf(name) > 0;
    }

    getFilterOptions(options: any = {}) {
        if (options) {
            Object.assign(this.categoryBrowseFilterOptions, options);
        }

        return this.categoryBrowseFilterOptions;
    }

    /**
     * Sets as many filter options as possible from a queryParamMap object
     * @param {ParamMap} params
     */
    setFilterOptionsFromQueryParams(params: ParamMap): void {
        this.queryParams = {};

        for (let optionName of Object.keys(this.categoryBrowseFilterOptions)) {
            if (params.keys.indexOf(optionName) < 0) {
                this.categoryBrowseFilterOptions[optionName] = "";
            }
        }

        for (let nextParam of params.keys) {
            let value: any = params.get(nextParam);

            if (this.categoryBrowseFilterOptions.hasOwnProperty(nextParam)) {
                // If the value we're referencing is a boolean (e.g. proGrade), a string value of 'true' equates to true
                // Any other value is false
                if (typeof (this.categoryBrowseFilterOptions[nextParam]) === 'boolean') {
                    value = (value === 'true');
                }

                this.categoryBrowseFilterOptions[nextParam] = value;
            }

            this.updateParam(nextParam, value, true);
        }
    }

    updateParam(name: string, value: any, replaceValue?: boolean, displayParam?: string, displayValue?: string) {
        let urlTree: UrlTree,
            params: any,
            param: any;

        if (this.updateUrlParams) {
            urlTree = this.router.parseUrl(this.router.url);
            params = urlTree.queryParams;
            param = urlTree.queryParams[name];
        } else {
            params = this.queryParams;
            param = this.queryParams[name];
        }

        if (!replaceValue) { replaceValue = false; }

        if (value != null) {
            value = value.toString();
        }

        if (param && value && !replaceValue) {
            let paramAsArray = param.split(',');
            // If this value is already in the array, we must be removing it
            if (paramAsArray.indexOf(value) >= 0) {
                paramAsArray.splice(paramAsArray.indexOf(value), 1);
            } else {
                paramAsArray.push(value);
            }
            // If the array is now empty (because we've unchecked all the possible values), delete this query param
            if (!paramAsArray.length) {
                delete params[name];
            } else {
                params[name] = paramAsArray.join(',');
            }
        } else if (value) {
            params[name] = value;
        } else {
            delete params[name];
        }

        this.paramUpdated$.emit({
            name: name,
            value: value,
            displayParam: displayParam,
            displayValue: displayValue
        });

        // Add in all the globally-set filter option params
        params = this.addFilterOptionParams(params);

        // Update the service's stored list of query params

        this.queryParams = params;

        if (this.updateUrlParams) {
            // Use history.replaceState() instead history.pushState() so we don't pollute the
            // browser history with tons of different query string param states
            this.router.navigateByUrl(urlTree, { replaceUrl: true });
        }
    }

    joinParamValues(params: any) {
        for (let nextKey of Object.keys(params)) {
            let value: any = params[nextKey];
            if (Array.isArray(value)) {
                params[nextKey] = value.join(',');
            }
        }

        return params;
    }

    clearParams(params: string[]) {
        let urlTree: UrlTree = this.router.parseUrl(this.router.url);

        console.log(urlTree);

        for (let param of params) {
            console.log(param);
            if (this.categoryBrowseFilterOptions.hasOwnProperty(param)) {
                this.categoryBrowseFilterOptions[param] = "";
            }
            delete urlTree.queryParams[param];
            this.cleared$.emit(param);
        }

        // Add in all the globally-set filter option params
        urlTree = this.addFilterOptionParams(urlTree);
        console.log(urlTree);

        this.queryParams = urlTree.queryParams;

        if (this.updateUrlParams) {
            // Use history.replaceState() instead history.pushState() so we don't pollute the
            // browser history with tons of different query string param states
            this.router.navigateByUrl(urlTree, { replaceUrl: true });
        }
    }

    clearAllParams() {
        let filters = this.getFilterCategoryRequestValues();
        let params = filters.map(f => f.name);
        let urlTree: UrlTree = this.router.parseUrl(this.router.url);

        for (let param of params) {
            if (this.categoryBrowseFilterOptions.hasOwnProperty(param)) {
                this.categoryBrowseFilterOptions[param] = "";
            }
            delete urlTree.queryParams[param];
            this.cleared$.emit(param);
        }

        // Add in all the globally-set filter option params
        urlTree = this.addFilterOptionParams(urlTree);

        this.queryParams = urlTree.queryParams;

        // Use history.replaceState() instead history.pushState() so we don't pollute the
        // browser history with tons of different query string param states
        this.router.navigateByUrl(urlTree, { replaceUrl: true });

    }

    addFilterOptionParams(params: any): any {
        if (this.categoryBrowseFilterOptions.tab) {
            params['tab'] = this.categoryBrowseFilterOptions.tab.toString();
        }

        if (this.categoryBrowseFilterOptions.finish) {
            params['finish'] = this.categoryBrowseFilterOptions.finish.toString();
        }

        // Do we need this for KWS? It causes tag removal of filters to prevent removal of types
        // if (this.categoryBrowseFilterOptions.type) {
        //     params['type'] = this.categoryBrowseFilterOptions.type.toString();
        // }

        if (this.categoryBrowseFilterOptions.sort) {
            params['sort'] = this.categoryBrowseFilterOptions.sort;
        }

        return params;
    }

    getFilterOptionParams(): any {
        let params: any = {};


        if (this.categoryBrowseFilterOptions.tab) {
            params.tab = this.categoryBrowseFilterOptions.tab.toString();
        }

        if (this.categoryBrowseFilterOptions.finish) {
            params.finish = this.categoryBrowseFilterOptions.finish.toString();
        }

        if (this.categoryBrowseFilterOptions.sort) {
            params.sort = this.categoryBrowseFilterOptions.sort;
        }

        if (this.categoryBrowseFilterOptions.type) {
            params.type = this.categoryBrowseFilterOptions.type.toString();
        }

        return params;
    }

}



// WEBPACK FOOTER //
// ./src/ts/Apps/Common/Services/products.service.ts